// src/pages/NewsListPage.tsx
// Public news index — root domain, lists all published articles

import React, { useEffect, useState } from 'react';
import axiosClient from '../api/axiosClient';
import Navbar from '../components/layout/Navbar';
import '../styles/news-detail.css';
import '../styles/news-list.css';

const SUBDOMAIN_BASE = process.env.REACT_APP_SUBDOMAIN_BASE || '.summitleague.gg';
const PROTOCOL = window.location.protocol;
const ARENA_URL = `${PROTOCOL}//hi${SUBDOMAIN_BASE}`;

interface NewsPost {
  post_id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  featured_image_url: string | null;
  categories: string[];
  published_at: string;
}

interface Pagination {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const NewsListPage: React.FC = () => {
  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axiosClient
      .get(`/news?page=${page}&limit=12`)
      .then((res) => {
        setPosts(res.data.data?.posts ?? []);
        setPagination(res.data.data?.pagination ?? null);
      })
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page]);

  return (
    <div className="sl-nd">
      <Navbar />

      <main className="sl-nl-main">
        <h1 className="sl-nl-title">News</h1>

        {loading && (
          <div className="sl-nl-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div className="sl-nl-card sl-nl-card--loading" key={i} />
            ))}
          </div>
        )}

        {!loading && posts.length === 0 && (
          <p className="sl-nl-empty">No articles published yet.</p>
        )}

        {!loading && posts.length > 0 && (
          <div className="sl-nl-grid">
            {posts.map((post) => (
              <a className="sl-nl-card" href={`/news/${post.slug}`} key={post.post_id}>
                <div className="sl-nl-card-img">
                  {post.featured_image_url ? (
                    <img
                      src={post.featured_image_url}
                      alt={post.title}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="sl-nl-card-img-placeholder" />
                  )}
                </div>
                <div className="sl-nl-card-body">
                  {post.categories?.[0] && (
                    <span className="sl-nl-card-category">{post.categories[0]}</span>
                  )}
                  <h3 className="sl-nl-card-title">{post.title}</h3>
                  {post.excerpt && <p className="sl-nl-card-excerpt">{post.excerpt}</p>}
                  <span className="sl-nl-card-date">{formatDate(post.published_at)}</span>
                </div>
              </a>
            ))}
          </div>
        )}

        {pagination && pagination.totalPages > 1 && (
          <div className="sl-nl-pagination">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ← Prev
            </button>
            <span className="sl-nl-pagination-status">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              type="button"
              disabled={page >= pagination.totalPages}
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            >
              Next →
            </button>
          </div>
        )}
      </main>

      <footer className="sl-nd-footer">
        &copy; {new Date().getFullYear()} Summit League &middot;{' '}
        <a href={`${ARENA_URL}/auth`}>Sign In</a> &middot;{' '}
        <a href="/legal">Legal</a>
      </footer>
    </div>
  );
};

export default NewsListPage;
